import React, {
  Children,
  ComponentPropsWithoutRef,
  useEffect,
  useMemo,
  useState,
} from "react";

import cn from "utils/cn";

import MoreContentDropDown from "components/common/MoreContentDropDown";
import Button from "../Button/Button";
import LinkItem from "../Button/LinkItem";

type TButton = ComponentPropsWithoutRef<typeof Button>;
type TLinkItem = ComponentPropsWithoutRef<typeof LinkItem>;

export type ButtonsGroupButton = {
  containerClassName?: string;
  label?: string;
  slug?: string;
} & (
  | ({
      type: "link";
      slug: string;
    } & Partial<TLinkItem>)
  | Partial<TButton>
);

interface ButtonsGroupProps {
  buttons: ButtonsGroupButton[];
  className?: string;
  isDivided?: boolean;
  children?: React.ReactNode;
  dropDownClassName?: string;
  moreBtnClassName?: string;
  hasMenuFullWidth?: boolean;
  removeBackground?: boolean;
  dropdownIconClassName?: string;
  position?: "top" | "bottom" | "left" | "right";
  trimAt?:
    | number
    | {
        desktop: number;
        mobile: number;
      };
}

// if trimAt is at 2, but there are 3 buttons, the first button might be hidden, so we need to trimAt 3/
// @ts-ignore
const recursiveTrimAt = (
  buttons: ButtonsGroupProps["buttons"],
  trimAt: number,
  childrenProp: ButtonsGroupProps["children"] = null,
) => {
  // @ts-ignore
  let trimAtValue: number = trimAt;
  // @ts-ignore
  if (trimAt > 0) {
    const howManyChildren = Children?.count(childrenProp);
    trimAtValue = trimAt - howManyChildren;
  }
  // if provided trimAt is 0, we want to show all buttons
  const coreButtons = [];
  const moreButtons = trimAt === 0 ? [...buttons] : [];
  if (trimAt > 0) {
    for (let i = 0; i < buttons.length; i++) {
      if (buttons[i].hidden) continue;

      if (trimAtValue === 0) {
        // spread the rest of the buttons
        moreButtons.push(...buttons.slice(i));
        break;
      } else {
        coreButtons.push(buttons[i]);
        trimAtValue--;
      }
    }
  }

  return {
    coreButtons: coreButtons.reverse(),
    moreButtons: moreButtons.reverse(),
  };
};

const ButtonsGroup = ({
  buttons,
  className,
  children,
  trimAt = 2,
  isDivided = false,
  dropDownClassName,
  removeBackground,
  moreBtnClassName,
  hasMenuFullWidth,
  position,
  dropdownIconClassName,
}: ButtonsGroupProps) => {
  const [responsiveButtons, setResponsiveButtons] = useState<{
    core: ButtonsGroupProps["buttons"];
    more: ButtonsGroupProps["buttons"];
  }>({
    core: [],
    more: [],
  });

  const [menuPosition, setMenuPosition] = useState(position || "right");

  useEffect(() => {
    // When the screen is resized, we want to re-calculate the buttons
    const handleResize = () => {
      if (!position) {
        if (["left", "right"].includes(menuPosition)) {
          if (window.innerWidth <= 768) {
            setMenuPosition("left");
          } else {
            setMenuPosition("right");
          }
        }
      }

      let trimAtValue: number;
      if (typeof trimAt === "object") {
        trimAtValue = window.innerWidth > 768 ? trimAt.desktop : trimAt.mobile;
      } else {
        if (trimAt === 0) trimAtValue = trimAt;
        else {
          if (window.innerWidth > 768) {
            trimAtValue = trimAt;
          } else {
            trimAtValue = 2;
          }
        }
      }

      const { coreButtons, moreButtons } = recursiveTrimAt(
        buttons,
        trimAtValue,
        children,
      );

      setResponsiveButtons({ core: coreButtons, more: moreButtons });
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [buttons]);
  // @ts-ignore
  const hasVisibleMoreButtons = useMemo(
    () => !responsiveButtons?.more?.every((button) => button.hidden),
    [responsiveButtons],
  );
  // @ts-ignore
  const visibleUnitSize = useMemo(() => {
    return (
      // Find the first button that has a size, and use that size, to unite all buttons
      // @ts-ignore
      responsiveButtons?.core?.find((button) => button.size)?.size || "large"
    );
  }, [responsiveButtons.core]);

  const BaseButton = useMemo(() => {
    return ({ button, ...args }: { button: ButtonsGroupButton; args: any }) => {
      if (button.hidden) return null;
      const Component =
        button.type === "link" || button.slug ? LinkItem : Button;

      return (
        <div className={cn(button.containerClassName)} test-id="V7IYlQjQ">
          {/* @ts-ignore */}

          <Component
            {...args}
            {...button}
            className={cn("whitespace-nowrap", button.className, {
              "border-[#2E2E2E] !bg-[#252525] text-white min-h-10":
                button.variant === "secondary",
            })}
            test-id="x0zlMQ9S"
          >
            {button.children || button.label}
          </Component>
        </div>
      );
    };
  }, []);

  return (
    <div
      className={cn(
        "flex",
        {
          "border-[#2E2E2E] border border-none min-h-[35px] h-[40px]":
            buttons.length > 1,
          "divide-x-2 divide-c-gray-600": isDivided && buttons.length > 1,
        },
        className,
      )}
      test-id="lUES4c27"
    >
      {responsiveButtons.more.length > 0 && hasVisibleMoreButtons && (
        <MoreContentDropDown
          position={menuPosition}
          dropdownIconClassName={dropdownIconClassName}
          shouldCalculateMaxHeight={false}
          removeBackground={removeBackground}
          anchorProps={{
            // @ts-ignore
            size: visibleUnitSize,
            // className: `${moreBtnClassName}
            // border border-[#2E2E2E] !bg-[#252525]  w-[30px] sm:w-10 h-[35px] sm:h-10 p-0 min-h-[35px] max-h-[35px] sm:min-h-[48px] sm:max-h-[48px] `,
            className: cn(
              "border border-[#2E2E2E] sm:w-10 w-[30px] sm:min-h-[48px] min-h-[32px]",
              moreBtnClassName,
              {
                "bg-[#252525]": !removeBackground,
              },
            ),

            id: "more-buttons",
          }}
          className={cn("space-y-s5 h-full", dropDownClassName)}
          scrollable={false}
          hasMenuFullWidth={hasMenuFullWidth}
          test-id="6rvfOeRq"
        >
          {responsiveButtons.more.map((button, i) => {
            return (
              <BaseButton
                key={i}
                button={button}
                // @ts-ignore
                variant="secondary"
                size="large"
                test-id="ME3j5dOj"
              />

              // <div key={i} className={button.className}>
              //   {/* @ts-ignore */}
              //   <Component
              //     variant="secondary"
              //     size="large"
              //     {...button}
              //     className={cn("whitespace-nowrap", button.className)}
              //   >
              //     {button.children || button.label}
              //   </Component>
              // </div>
            );
          })}
        </MoreContentDropDown>
      )}

      {responsiveButtons.core.map((button, i, arr) => {
        // Since we reverse the buttons, the first button is the last button in the array
        // We do so because of the rendering order.
        const isFirstButton = i === arr.length - 1;

        const shouldAddWidth =
          isFirstButton &&
          (button.className?.includes("w-") === false || !button.className);

        const buttonArgs = {
          ...button,
          className: cn(button.className, {
            // if the first button doesn't have a width, we want to give it a width of 24
            // Usually the first btn is the save, submit, and update button
            "sm:min-w-[6rem]": shouldAddWidth,
          }),
        };

        return (
          <BaseButton
            key={i}
            button={buttonArgs}
            // @ts-ignore
            variant="secondary-stroked"
            size={visibleUnitSize}
            test-id="33g4QO7F"
          />

          // <div key={i} className={button.containerClassName}>
          //   {/* @ts-ignore */}
          //   <Component
          //     variant="secondary-stroked"
          //     size={visibleUnitSize}
          //     {...button}
          //     className={cn("whitespace-nowrap", button.className)}
          //   >
          //     {button.children || button.label}
          //   </Component>
          // </div>
        );
      })}

      {children}
    </div>
  );
};

export default ButtonsGroup;
